import {
  Box,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Image,
  Text as ChakraText,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "../../components/Card/Card";
import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";
import { FaAngleRight, FaArrowLeft } from "react-icons/fa";
import { saveAs } from "file-saver";
import handlePrintReport from "../Reports/ImageSummaryReport";
import { exportNestedToCsv } from "../../Utils/ExcelExport";
import AgGridWrapper from "../../components/AgGrid/AgGridWrapper";

export default function DetailView() {
  const textColor = useColorModeValue("gray.700", "white");
  const history = useHistory();
  const location = useLocation();
  const [selectedStore, setSelectedStore] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  const query = new URLSearchParams(location.search);
  const runId = query.get("runId") || "8";

  useEffect(() => {
    const fetchTableData = async (retryCount = 0) => {
      setLoading(true);
      setError(null);
      try {
        const url = `${API_CONFIG.BASE_URL}${
          API_CONFIG.ENDPOINTS.DETAILED_VIEW.IMAGE_COUNT
        }?runId=${encodeURIComponent(runId)}`;
        const response = await apiClient.get(url);
        const apiData = Array.isArray(response.data)
          ? response.data
          : [response.data];

        // Transform and fetch additional data per-row
        const transformedData = await Promise.all(
          apiData.map(async (item, idx) => {
            const imageName = item.fileName ?? "";
            const sno = item.sno ?? `SNO${idx + 1}`;
            const totalCount = item.totalCount ?? 0;
            const storeName = item.store?.storeName || "Unknown Store";

            let additionalData = [];
            let s3pathActualFile = "";
            let s3pathAnnotatedFile = "";
            try {
              // 1. Fetch additionalData (product info, s3 keys)
              const apiForProducts = encodeURI(
                `${
                  API_CONFIG.BASE_URL
                }${API_CONFIG.ENDPOINTS.BATCH_TRANSACTIONS.PRODUCTS_BY_IMAGE.replace(
                  ":runId",
                  runId
                ).replace(":imageName", imageName)}`
              );
              const secondResponse = await apiClient.get(apiForProducts);
              const secondDataArr = Array.isArray(secondResponse.data)
                ? secondResponse.data
                : [secondResponse.data];
              additionalData = secondDataArr.map((data) => ({
                file: data.fileName ?? "N/A",
                company:
                  data.product?.productCompany?.productCompanyName ?? "Unknown",
                brand:
                  data.product?.productBrand?.productBrandName ?? "Unknown",
                type: data.product?.packType ?? "Unknown",
                size: data.product?.packSize ?? "Unknown",
                count: data.productCount ?? 0,
              }));

              // 2. Use s3pathActualFile and s3pathAnnotatedFile from response!
              // For images, always use keys from secondDataArr[0] (first match).
              const s3actual = secondDataArr?.[0]?.s3pathActualFile;
              const s3annotated = secondDataArr?.[0]?.s3pathAnnotatedFile;

              // 3. Get presigned URLs only if key exists
              if (s3actual) {
                const { data: actualUrl } = await apiClient.get(
                  `${API_CONFIG.ENDPOINTS.STORE_REPORT.S3_PRESIGNED_URL}?fileName=${encodeURIComponent(
                    s3actual
                  )}&bucketName=imageinsightimagesbeverages&operation=download`
                );
                s3pathActualFile = actualUrl;
              }
              if (s3annotated) {
                const { data: annotatedUrl } = await apiClient.get(
                  `${API_CONFIG.ENDPOINTS.STORE_REPORT.S3_PRESIGNED_URL}?fileName=${encodeURIComponent(
                    s3annotated
                  )}&bucketName=imageinsightimagesbeverages&operation=download`
                );
                s3pathAnnotatedFile = annotatedUrl;
              }
            } catch (secondError) {
              additionalData = [];
            }

            return {
              rowIndex: idx,
              sno,
              storeName,
              imageName,
              count: totalCount.toString(),
              additionalData,
              s3pathActualFile,
              s3pathAnnotatedFile,
            };
          })
        );

        setTableData(transformedData);
      } catch (err) {
        if (retryCount < 2) {
          setTimeout(() => fetchTableData(retryCount + 1), 1000);
        } else {
          setError("Failed to fetch data after retries.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTableData();
  }, [runId]);

  const handleViewDetails = (rowData) => {
    setSelectedRowData(rowData);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedRowData(null);
  };

  const handleBackToDashboard = () => {
    history.push("/admin/dashboard");
  };

  const handleExportData = () => {
    const storeName = selectedStore ? selectedStore.storeName : "unknown";
    exportNestedToCsv({
      headers: ["S.No", "StoreName", "Image Name", "Count", "Nested File", "Nested Company", "Nested Brand", "Nested Pack Type", "Nested Pack Size", "Nested Count"],
      data: tableData,
      getMainValues: (row) => [row.sno, row.storeName, row.imageName, row.count],
      getNestedValues: (nested) => [nested.file || "", nested.company || "", nested.brand || "", nested.type || "", nested.size || "", nested.count || ""],
      fileName: `detail_view_${storeName}`,
    });
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Additional",
        field: "expand",
        width: 80,
        cellRenderer: (params) => {
          const hasAdditionalData =
            params.data?.additionalData &&
            Array.isArray(params.data.additionalData) &&
            params.data.additionalData.length > 0;
          if (hasAdditionalData) {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <div
                  onClick={() => handleViewDetails(params.data)}
                  style={{ cursor: "pointer" }}
                >
                  <FaAngleRight size={20} />
                </div>
              </div>
            );
          }
          return null;
        },
        sortable: false,
        filter: false,
      },
      {
        headerName: "S.No",
        field: "sno",
        cellClass: "ag-numeric-cell",
        sortable: true,
        filter: true,
        width: 150,
      },
      {
        headerName: "Store Name",
        flex:1,
        field: "storeName",
        minWidth: 80,
        sortable: true,
        filter: true,
        
        valueFormatter: (params) => params.value || "N/A",
      },
      {
        headerName: "Image Name",
        field: "imageName",
        flex:1,
          // increased
        minWidth: 80, 
        sortable: true,
        filter: true,
      },
      {
        headerName: "Count",
        field: "count",
        width:150,
        sortable: true,
        filter: "agNumberColumnFilter",
        cellClass: "ag-numeric-cell",
        
      },
    ],
    []
  );

  const nestedColumnDefs = useMemo(
    () => [
      {
        headerName: "Company",
        field: "company",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Brand",
        field: "brand",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Pack Type",
        field: "type",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Pack Size",
        field: "size",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Count",
        field: "count",
        width:90,
        sortable: true,
        filter: "agNumberColumnFilter",
        cellClass: "ag-numeric-cell",
        valueFormatter: (params) => params.value?.toString() || "0",
      },
    ],
    []
  );

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Card mb="20px" p="20px">
        <Flex justify="space-between" align="center" mb="20px">
          <Flex alignItems="center" gap="4">
            <Button
              leftIcon={<FaArrowLeft />}
              onClick={handleBackToDashboard}
              size="sm"
              colorScheme="blue"
              variant="outline"
              borderRadius="5px"
            >
              Back
            </Button>
          </Flex>
          <Flex alignItems="center" gap="10px">
            <Button
              onClick={() =>
                handlePrintReport(
                  tableData,
                  selectedStore?.storeName || "unknown"
                )
              }
              size="sm"
              colorScheme="gray"
              variant="outline"
              borderRadius="5px"
              fontSize="sm"
              isDisabled={loading || error || tableData.length === 0}
            >
              Print Report
            </Button>
            <Button
              onClick={handleExportData}
              size="sm"
              bg="black"
              color="white"
              borderRadius="5px"
              fontSize="sm"
              _hover={{ bg: "gray.700" }}
              isDisabled={loading || error || tableData.length === 0}
            >
              Export Data
            </Button>
          </Flex>
        </Flex>
        <AgGridWrapper
          rowData={tableData}
          columnDefs={columnDefs}
          loading={loading}
          height="650px"
          noRowsMessage={error || "No data available"}
          extraGridProps={{ pagination: true, paginationPageSize: 8, headerHeight: 70 }}
        />
      </Card>

      <Modal
        isOpen={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        size="full"
      >
        <ModalOverlay />
        <ModalContent
          p={0}
          m={0}
          maxW="100vw"
          minW="100vw"
          maxH="100vh"
          minH="100vh"
          borderRadius="0"
        >
          <ModalHeader>Additional Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflow="auto">
            {selectedRowData && (
              <Box mb="20px">
                <Flex justify="space-between" align="center">
                  <ChakraText fontSize="lg" fontWeight="bold">
                    Store Name: {selectedRowData.storeName || "N/A"}
                  </ChakraText>
                  <ChakraText fontSize="lg" fontWeight="bold">
                    File Name: {selectedRowData.imageName || "N/A"}
                  </ChakraText>
                </Flex>
              </Box>
            )}
            {selectedRowData && (
              <Box>
                <Flex direction="row" gap="20px" height="600px" alignItems="flex-start">
                  {/* Grid Section */}
                  {selectedRowData &&
                  Array.isArray(selectedRowData.additionalData) &&
                  selectedRowData.additionalData.length > 0 ? (
                    <Box height="100%" width="50%">
                      <AgGridWrapper
                        rowData={selectedRowData.additionalData}
                        columnDefs={nestedColumnDefs}
                        height="100%"
                        noRowsMessage="No additional data available"
                        extraGridProps={{ pagination: true, paginationPageSize: 8, headerHeight: 70 }}
                      />
                    </Box>
                  ) : (
                    <ChakraText>No additional data available.</ChakraText>
                  )}
                  {/* Images Section */}
                  <Flex direction="column" gap="10px" alignItems="center" marginLeft="80px">
                    <ChakraText fontSize="lg" fontWeight="bold">
                      Actual Image
                    </ChakraText>
                    <Image
                      src={
                        selectedRowData.s3pathActualFile ||
                        "https://via.placeholder.com/300?text=No+Actual+Image"
                      }
                      alt="Actual"
                      maxW="800px"
                      maxH="700px"
                      w="120%"
                      h="700px"
                      objectFit="contain"
                      borderRadius="md"
                    />
                  </Flex>
                  <Flex direction="column" gap="10px" alignItems="center">
                    <ChakraText fontSize="lg" fontWeight="bold">
                      Annotated Image
                    </ChakraText>
                    <Image
                      src={
                        selectedRowData.s3pathAnnotatedFile ||
                        "https://via.placeholder.com/300?text=No+Annotated+Image"
                      }
                      alt="Annotated"
                      maxW="800px"
                      maxH="700px"
                      w="120%"
                      h="700px"
                      objectFit="contain"
                      borderRadius="md"
                    />
                  </Flex>
                </Flex>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleCloseDetailsModal}
              size="md"
              borderRadius="5px"
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}